import { useEffect, useState, useCallback } from "react";
import Layout from "@/components/Layout";
import api from "@/lib/api";
import { useClientScope } from "@/hooks/useClientScope";
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, BarChart, Bar,
} from "recharts";
import { BarChart3, Loader2, ShieldOff, MessageSquareOff, Clock, Bot } from "lucide-react";

const c = (name, stop) => `rgb(var(--c-${name}-${stop}))`;
const CAT_COLORS = {
  critical: c("oxblood", 500), spam: c("ochre", 500), abuse: c("plum", 500),
  rival_criticism: c("steel", 500), neutral: c("accent", 500), other: c("stone", 400),
};
const LANG_COLORS = [c("accent", 600), c("stone", 500), c("accent", 400), c("steel", 400), c("stone", 300)];
const ACCENT = c("accent", 600);
const REMOVED = c("oxblood", 500);
const GRID = c("stone", 200);
const TICK = { fontSize: 11, fill: c("stone", 500) };
const TOOLTIP = {
  contentStyle: { background: "hsl(var(--popover))", border: "1px solid hsl(var(--border))", borderRadius: 8, fontSize: 12, color: "hsl(var(--foreground))" },
  labelStyle: { color: "hsl(var(--muted-foreground))" },
  cursor: { fill: c("stone", 100) },
};

export default function Reports() {
  const { isSuper, clientId, ready, scopeParam, ClientPicker } = useClientScope();
  const [data, setData] = useState(null);
  const [days, setDays] = useState(30);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    if (isSuper && !clientId) { setData(null); setLoading(false); return; }
    setLoading(true);
    try {
      const q = scopeParam ? `?${scopeParam}&days=${days}` : `?days=${days}`;
      const { data } = await api.get(`/reports/summary${q}`);
      setData(data);
    } finally {
      setLoading(false);
    }
  }, [isSuper, clientId, scopeParam, days]);

  useEffect(() => { if (ready) load(); }, [ready, load]);

  const totals = data?.totals || {};
  const kpis = [
    { label: "Total comments", value: totals.total ?? 0, icon: MessageSquareOff, tone: "text-slate-700" },
    { label: "Removed", value: totals.removed ?? 0, icon: ShieldOff, tone: "text-red-500" },
    { label: "Auto-hidden", value: totals.auto ?? 0, icon: Bot, tone: "text-indigo-500" },
    { label: "Awaiting review", value: totals.needs_review ?? 0, icon: Clock, tone: "text-amber-500" },
  ];

  return (
    <Layout title="Reports" subtitle="Removals over time, top critical topics, and per-post breakdown.">
      <div className="space-y-6 animate-rise">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <ClientPicker />
          <div className="flex gap-1.5 bg-card rounded-lg p-1 border border-slate-200 shadow-sm" data-testid="range-tabs">
            {[7, 30, 90].map((d) => (
              <button key={d} data-testid={`range-${d}`} onClick={() => setDays(d)}
                className={`px-4 py-1.5 rounded-lg text-sm font-semibold transition ${days === d ? "bg-slate-900 text-slate-50" : "text-slate-500 hover:text-slate-800"}`}>
                {d}d
              </button>
            ))}
          </div>
        </div>

        {isSuper && !clientId ? (
          <Empty text="Select a client to view its reports." />
        ) : loading ? (
          <div className="flex justify-center py-24"><Loader2 className="w-7 h-7 animate-spin text-indigo-500" /></div>
        ) : !data || totals.total === 0 ? (
          <Empty text="No comment data yet. Connect an account and pull comments to see reports." />
        ) : (
          <>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              {kpis.map((k) => (
                <div key={k.label} data-testid={`kpi-${k.label}`} className="bg-card rounded-2xl border border-slate-200/70 p-5 shadow-sm">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-slate-500">{k.label}</span>
                    <k.icon className={`w-4 h-4 ${k.tone}`} />
                  </div>
                  <div className={`text-3xl font-bold mt-2 ${k.tone}`}>{k.value}</div>
                </div>
              ))}
            </div>

            <div className="bg-card rounded-2xl border border-slate-200/70 p-6 shadow-sm">
              <h3 className="font-semibold text-slate-800 mb-4">Removals over time</h3>
              <ResponsiveContainer width="100%" height={260}>
                <AreaChart data={data.timeseries} margin={{ left: -20, right: 10, top: 5 }}>
                  <defs>
                    <linearGradient id="rem" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor={REMOVED} stopOpacity={0.35} />
                      <stop offset="95%" stopColor={REMOVED} stopOpacity={0} />
                    </linearGradient>
                    <linearGradient id="tot" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor={ACCENT} stopOpacity={0.25} />
                      <stop offset="95%" stopColor={ACCENT} stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke={GRID} />
                  <XAxis dataKey="date" tickFormatter={(v) => v.slice(5)} tick={TICK} />
                  <YAxis allowDecimals={false} tick={TICK} />
                  <Tooltip {...TOOLTIP} />
                  <Area type="monotone" dataKey="total" stroke={ACCENT} fill="url(#tot)" strokeWidth={2} name="All comments" />
                  <Area type="monotone" dataKey="removed" stroke={REMOVED} fill="url(#rem)" strokeWidth={2} name="Removed" />
                </AreaChart>
              </ResponsiveContainer>
            </div>

            <div className="grid lg:grid-cols-2 gap-4">
              <div className="bg-card rounded-2xl border border-slate-200/70 p-6 shadow-sm">
                <h3 className="font-semibold text-slate-800 mb-4">Top critical topics</h3>
                {data.topics.length === 0 ? <p className="text-sm text-slate-400">No removals yet.</p> : (
                  <ResponsiveContainer width="100%" height={240}>
                    <PieChart>
                      <Pie data={data.topics} dataKey="count" nameKey="category" innerRadius={55} outerRadius={90} paddingAngle={2}>
                        {data.topics.map((t) => <Cell key={t.category} fill={CAT_COLORS[t.category] || CAT_COLORS.other} />)}
                      </Pie>
                      <Tooltip {...TOOLTIP} />
                    </PieChart>
                  </ResponsiveContainer>
                )}
                <div className="flex flex-wrap gap-3 mt-2">
                  {data.topics.map((t) => (
                    <div key={t.category} className="flex items-center gap-1.5 text-sm">
                      <span className="w-3 h-3 rounded-full" style={{ background: CAT_COLORS[t.category] || CAT_COLORS.other }} />
                      <span className="text-slate-600 capitalize">{t.category.replace("_", " ")}</span>
                      <span className="text-slate-400 font-mono">{t.count}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="bg-card rounded-2xl border border-slate-200/70 p-6 shadow-sm">
                <h3 className="font-semibold text-slate-800 mb-4">Removals by language</h3>
                {data.languages.length === 0 ? <p className="text-sm text-slate-400">No removals yet.</p> : (
                  <ResponsiveContainer width="100%" height={240}>
                    <BarChart data={data.languages} margin={{ left: -20, top: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke={GRID} vertical={false} />
                      <XAxis dataKey="language" tick={TICK} />
                      <YAxis allowDecimals={false} tick={TICK} />
                      <Tooltip {...TOOLTIP} />
                      <Bar dataKey="count" radius={[6, 6, 0, 0]}>
                        {data.languages.map((l, i) => <Cell key={l.language} fill={LANG_COLORS[i % LANG_COLORS.length]} />)}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                )}
              </div>
            </div>

            <div className="bg-card rounded-2xl border border-slate-200/70 p-6 shadow-sm">
              <h3 className="font-semibold text-slate-800 mb-4">Per-post breakdown</h3>
              {data.per_post.length === 0 ? <p className="text-sm text-slate-400">No data.</p> : (
                <div className="space-y-3" data-testid="per-post-list">
                  {data.per_post.map((p) => {
                    const pct = p.total ? Math.round((p.removed / p.total) * 100) : 0;
                    return (
                      <div key={p.post_id} className="flex items-center gap-4" data-testid="per-post-row">
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium text-slate-700 truncate">{p.caption || p.post_id}</div>
                          <div className="h-2 mt-1.5 rounded-full bg-slate-100 overflow-hidden">
                            <div className="h-full bg-red-400 rounded-lg" style={{ width: `${pct}%` }} />
                          </div>
                        </div>
                        <div className="text-sm text-slate-500 whitespace-nowrap">
                          <span className="font-semibold text-red-500">{p.removed}</span> / {p.total} removed
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </Layout>
  );
}

function Empty({ text }) {
  return (
    <div className="bg-card rounded-2xl border border-dashed border-slate-300 p-16 text-center">
      <BarChart3 className="w-10 h-10 text-slate-300 mx-auto mb-3" />
      <p className="text-slate-500">{text}</p>
    </div>
  );
}
